import { gql } from '@apollo/client';
import { Capacitor } from '@capacitor/core';
import type { History, Location } from 'history';
import type { TFunction } from 'i18next';
import { flow, forEach, includes } from 'lodash';
import type { ReactNode } from 'react';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Divider } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';

import { CIAnalytics } from 'common/analytics/analytics.provider';
import { AuthActions } from 'common/auth/actions';
import type { AuthState, PatientAccess } from 'common/auth/types';
import { Form } from 'common/component/ValidationFormAndField';
import { featureFlags } from 'common/featureFlags/featureFlags';
import { nonAuthClient } from 'common/graphql/client';
import { AuthUserType, CognitoErrorMessage } from 'common/graphql/types';
import withTranslate from 'common/i18n/i18n';
import { AuditCreationEventsEnum, AuditEntityIdEnum } from 'common/log/auditevents';
import { CIAuditLogger } from 'common/log/ci.auditlogger';
import { CILogger } from 'common/log/log.provider';
import { getDeviceSerialNumber } from 'common/native-app-support/device-initializer/native.device.initializer';
import { browserHistory } from 'common/routing/browser_history';
import type { SiteStudy } from 'common/types/site';
import { minLength, required } from 'common/utils/validations';
import { CISecondaryButton } from 'common/viewcomp/forms/buttons';
import { CITextField } from 'common/viewcomp/forms/fields/text';
import type { PageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { withPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import type { DialogContentMode } from 'common/viewcomp/modal/types';

import oktaAuth from '../okta/oktaAuth';
import { guardedOktaSignInWithRedirect } from '../okta/utils/oktaUtils';

import { withAuthLayout } from './authLayout.page';
import ChangePassModal from './changepass/changepass.modal';
import FirstOfflineLoginModal from './firstofflinelogin/firstofflinelogin.modal';
import LockouTimeoutDialog from './lockout_timeout_dialog';
import './login.page.scss';
import { DEFAULT_PATH, redirectToLastPagesOpened } from './login.utils';

interface Props {
    t: TFunction;
    history: History;
    auth: AuthState;
    routes: Array<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        component: any;
        path: string;
        permisssions?: string[];
        role: string[];
    }>;
    loginRequest: (username, password) => void;
    switchUserLoginRequest: (username, password) => void;
    resetComplete?: () => void;
    changePasswordSucceeded: Function;
    changePasswordRequest: Function;
    clearError: Function;
    location: Location;
    pageStatus: PageStatus;
    setSwitchUserDialogContent?: (mode: DialogContentMode) => void;
    checkStudyAndSiteBetweenUsers?: () => void;
    logoutRequest?: Function;
    finishSwitchUser?: Function;
    handleSwitchUserTempUser?: () => void;
    handleSetErrorMessage?: (status: string | null) => void;
    handlePatientAccess?: (updatePatientAccess: PatientAccess) => void; //dispatch action from redux
    updateActiveStudySite: (SiteStudy: SiteStudy) => void;
}

interface State {
    username: string;
    password: string;
    errorMessage: string;
    autofilled: boolean;
    isLoading: boolean;
    showPassword: boolean;
    email: string;
    NEW_PASS_REQUIRED: boolean;
    MFA_REQUIRED: boolean;
    TOTP_REQUIRED: boolean;
    CUSTOM_CHALLENGE: boolean;
    MFA_SETUP: boolean;
    SELECT_MFA_TYPE: boolean;
    isLockOutModal: boolean;
    isTimerClose: boolean;

    isFirstOfflineLoginModal: boolean;
    switchUserLoginSuccess: boolean;
}

// TODO: same query in auth/queries.ts
const GetRemainingAttemptsQuery = gql`
    query LoginGetUserNumberOfLoginTriesBeforeLockout($username: String!) {
        getUserNumberOfLoginTriesBeforeLockout(username: $username)
    }
`;

export class LoginPageComponent extends Component<Props, State> {
    public constructor(props) {
        super(props);

        this.state = LoginPageComponent.getInitialState();

        this.handleAuthenticateRequest = this.handleAuthenticateRequest.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.closeLockOutModal = this.closeLockOutModal.bind(this);
        this.timerClose = this.timerClose.bind(this);
        this.clearErrorState = this.clearErrorState.bind(this);
        this.handleClickShowPassword = this.handleClickShowPassword.bind(this);
        this.closeFirstOfflineLoginModal = this.closeFirstOfflineLoginModal.bind(this);

        //Check bad login,
        this.handleSwitchUserForgotLink = this.handleSwitchUserForgotLink.bind(this);
    }

    private static getInitialState(): State {
        return {
            username: '',
            password: '',
            errorMessage: '',
            autofilled: false,
            isLoading: false,
            showPassword: false,
            email: '',
            NEW_PASS_REQUIRED: false,
            MFA_REQUIRED: false,
            TOTP_REQUIRED: false,
            CUSTOM_CHALLENGE: false,
            MFA_SETUP: false,
            SELECT_MFA_TYPE: false,
            isLockOutModal: false,
            isTimerClose: false,
            isFirstOfflineLoginModal: false,
            switchUserLoginSuccess: false,
        };
    }

    private clearErrorState(): void {
        this.props.clearError();
        this.setState({ errorMessage: '' });
    }

    private isNotLoginModal(): boolean {
        return includes(['/patientLogin', '/login', '/'], this.props?.location?.pathname);
    }

    public componentDidUpdate(_prevProps, prevState): void {
        let errorMessage = this.props.auth.error?.translationKey;
        if (this.state.isTimerClose) {
            this.props.pageStatus.dismiss();
        }

        if (errorMessage && !this.state.errorMessage && !prevState.errorMessage) {
            if (errorMessage === 'cognito.NotAuthorizedException') {
                let attemptsremaining = null;
                nonAuthClient
                    .query({
                        query: GetRemainingAttemptsQuery,
                        variables: {
                            username: this.state.username,
                        },
                    })
                    .then((res): void => {
                        attemptsremaining = res.data.getUserNumberOfLoginTriesBeforeLockout;
                    })
                    .catch((): void => {
                        attemptsremaining = null;
                    })
                    .finally(() => {
                        if (this.props.auth?.error?.message === CognitoErrorMessage.UserDisabled) {
                            errorMessage = 'cognito.UserDisabledException';
                        } else {
                            errorMessage =
                                attemptsremaining === 0
                                    ? 'cognito.UserLambdaValidationException'
                                    : this.props.auth.error?.translationKey;
                        }
                        this.setState({
                            errorMessage: errorMessage,
                            isLoading: false,
                            isLockOutModal: attemptsremaining === 0,
                        });

                        this.props.pageStatus.error(this.props.t(errorMessage), null, this.clearErrorState);

                        if (attemptsremaining === 0) {
                            CILogger.getInstance().info({
                                // kibana logging
                                message: `${this.state.username} was locked out for 10 minutes.`,
                                username: this.state.username,
                            });
                            // Added for audit log event when the user locked out
                            CIAuditLogger.getInstance().log(
                                AuditCreationEventsEnum.Logout,
                                `User ${this.state.username} was locked out of their account`,
                                null,
                                {
                                    relatedEntityName: AuditEntityIdEnum.User,
                                }
                            );
                        }
                    });
            } else if (errorMessage === 'native.USER_NAME_NOT_FOUND') {
                this.setState({
                    errorMessage: errorMessage,
                    isLoading: false,
                    isFirstOfflineLoginModal: true,
                });

                getDeviceSerialNumber().then((id: string) => {
                    CILogger.getInstance().info({
                        // kibana logging
                        message: `Non-existing username ${this.state.username} login was attempted in device ${id} when offline`,
                    });
                });
            } else {
                if (this.props.auth.error?.translationKey === 'cognito.LimitExceededException') {
                    errorMessage = 'cognito.LimitExceededException';
                }
                if (
                    this.props.auth.error?.translationKey === 'cognito.UserLambdaValidationException' &&
                    includes(this.props.auth.error?.message, 'PasswordExpired')
                ) {
                    errorMessage = 'cognito.PasswordExpired';
                } else if (
                    this.props.auth.error?.translationKey === 'cognito.UserLambdaValidationException' &&
                    includes(this.props.auth.error?.message, 'TooManyAttempts')
                ) {
                    errorMessage = 'cognito.UserLambdaValidationException';
                }
                this.setState({
                    errorMessage: errorMessage,
                    isLoading: false,
                });
                this.props.pageStatus.error(this.props.t(errorMessage), null, this.clearErrorState);
            }
        }
        if (this.props.auth.switchingUser && this.props.auth.complete && !this.state.switchUserLoginSuccess) {
            this.setState(
                {
                    switchUserLoginSuccess: true,
                },
                () => this.props.checkStudyAndSiteBetweenUsers()
            );
        }
        if (this.props.auth.complete && !this.state.switchUserLoginSuccess) {
            if (prevState?.isLoading) {
                this.setState({ isLoading: false });
            }
            if (!this.props.auth.switchingUser) {
                const targetPath = (this.props.location.state && this.props.location.state.target) || DEFAULT_PATH;
                // Check and Redirect to Saved location
                redirectToLastPagesOpened(
                    targetPath,
                    this.props.auth.activeUser,
                    this.props.updateActiveStudySite,
                    this.props.history
                );
            }
        }
    }

    private handleAuthenticateRequest({ username, password }): void {
        this.props.pageStatus?.dismiss(); // remove the previous page status error message
        // IF the user has opened login component in /login page,
        // Check switchingUser status, If `true` then this is error flow, because, user has navigated to this page from URL
        // after opening switch user pop up
        if (this.isNotLoginModal() && this.props.auth.switchingUser) {
            this.props.pageStatus.error(this.props.t('login.second_user_error'), null, this.clearErrorState);
            this.props.logoutRequest();
        } else {
            this.setState({ isLoading: true, switchUserLoginSuccess: false, username: username });
            if (this.props.auth.switchingUser && this.props.auth.activeStudySite?.activeSite) {
                this.props.handleSetErrorMessage?.(null);
                this.props.switchUserLoginRequest(username, password);
            } else {
                localStorage.removeItem('switchingUser');
                this.props.loginRequest(username, password);
            }
        }
    }

    private handleClickShowPassword(): void {
        this.setState({ showPassword: !this.state.showPassword });
    }

    /**
     * This page uses modals for various auth conditions returned by Cognito.  This method closes any/all open modal windows
     */
    private closeModal(): void {
        this.props.changePasswordSucceeded();
        this.setState({
            isLoading: false,
        });
    }

    private closeFirstOfflineLoginModal(): void {
        this.props.clearError();
        this.setState({ errorMessage: '', isFirstOfflineLoginModal: false });
    }

    private closeLockOutModal(bool, errBool): void {
        this.setState({
            errorMessage: errBool ? '' : this.state.errorMessage,
            isLockOutModal: bool,
        });
    }
    private timerClose(bool): void {
        this.setState({
            isTimerClose: bool,
        });
    }

    private handleSwitchUserForgotLink(): void {
        if (this.isNotLoginModal() && this.props.auth.switchingUser) {
            this.props.pageStatus.error(this.props.t('login.second_user_error'), null, this.clearErrorState);
            this.props.logoutRequest();
        } else {
            this.props?.setSwitchUserDialogContent('ForgotUsernamePassword');
        }
    }

    private handleBackToUserTypeSelection = (): void => {
        browserHistory.push('/');
    };

    /**
     * Responsible for setting page title when the component is mounted
     */
    public componentDidMount(): void {
        this.props.resetComplete();
        //reset tempUser state in auth_context
        this.props.handleSwitchUserTempUser();
        if (!this.props.auth.switchingUser) {
            document.title = this.props.t('login.page_title');
            CIAnalytics.getInstance().anonymousUser('initialize');
        }

        // Prefetch selected components
        // eslint-disable-next-line lodash/prefer-filter
        forEach(this.props.routes, (route) => {
            if (route.component?.preload) {
                route.component.preload();
            }
        });

        if (featureFlags.fxIntegration) {
            if (this.props.auth?.switchingUser) {
                // Coordinator switching to patient (landing from Okta)
                if (localStorage.getItem('switchUserAuthUser')) {
                    const authUser = JSON.parse(localStorage.getItem('switchUserAuthUser'));
                    localStorage.removeItem('switchUserAuthUser');

                    this.props.finishSwitchUser(authUser);
                }

                // Patient switching back to coordinator - redirect to Okta login
                if (
                    this.props.auth?.activeUser?.userType === AuthUserType.PATIENT &&
                    localStorage.getItem('postLoginRedirectUrl')
                ) {
                    this.setState({ isLoading: true });

                    guardedOktaSignInWithRedirect(oktaAuth)
                        .catch((e) => {
                            CILogger.getInstance().error('Error initiating Okta redirect:', e);
                        })
                        .finally(() => {
                            this.setState({ isLoading: false });
                        });
                }
            }
        }
    }

    /**
     * Render component to screen
     */
    public render(): ReactNode {
        const {
            t,
            auth: { error, submitted, switchingUser },
        } = this.props;
        const changePass = error && error.trigger === 'NEW_PASS_REQUIRED';
        const patientAccess = this.props.auth.patientAccess?.status !== 'IDLE';
        const patientActivated = this.props.auth.patientAccess?.activationStatus;

        const { showPassword, isLoading, isLockOutModal, isFirstOfflineLoginModal } = this.state;

        let patientInitialValues = {
            username: null,
            password: null,
        };

        if (patientAccess) {
            patientInitialValues = {
                username: this.props.auth.patientAccess?.currentPatientID,
                password: null,
            };
        }

        const showBackButton = featureFlags.fxIntegration && this.isNotLoginModal() && !switchingUser && !patientAccess;

        return (
            <>
                <div id="login_content" className={switchingUser ? 'switch_user_login_page' : ''}>
                    {showBackButton && (
                        <div className="login-top-bar">
                            <Button
                                id="back-to-user-type"
                                onClick={this.handleBackToUserTypeSelection}
                                className="back-to-user-type-button"
                                startIcon={<ArrowBackIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                {t('select_user_level_role_page.back_button')}
                            </Button>
                        </div>
                    )}
                    <h1 className="header" id="login_header">
                        {patientAccess
                            ? t('login.switch_log_in')
                            : switchingUser
                              ? t('login.switch_user')
                              : t('login.welcome_back')}
                    </h1>
                    <Form
                        onSubmit={this.handleAuthenticateRequest}
                        initialValues={patientInitialValues}
                        render={({ handleSubmit, invalid }): ReactNode => {
                            const disableButton = invalid;
                            const submitButtonClass =
                                (!submitted || this.props.auth.error) && !isLoading
                                    ? 'submit_button disabled-submit-button'
                                    : 'submit_button enabled-submit-button';

                            return (
                                <form id="loginForm" onSubmit={handleSubmit} noValidate>
                                    <CITextField
                                        className="login_input_field username_field"
                                        t={t}
                                        id="username"
                                        name="username"
                                        labelI18NKey={
                                            switchingUser ? 'login.site_username_label' : 'login.username_label'
                                        }
                                        type="text"
                                        margin="normal"
                                        fullWidth
                                        autoFocus
                                        required
                                        disabled={isLoading}
                                        validations={[required(), minLength(3, 'error.username_too_short')]}
                                    />
                                    <CITextField
                                        t={t}
                                        className="login_input_field"
                                        id="password"
                                        name="password"
                                        labelI18NKey="login.password_label"
                                        type={showPassword ? 'text' : 'password'}
                                        //type="text"
                                        margin="normal"
                                        fullWidth
                                        required
                                        maxLength={16} // temporary until specs are defined
                                        disabled={isLoading}
                                        validations={[required(), minLength(8, 'error.too_short')]}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    id="visibility_toggle"
                                                    aria-label={t('login.toggle_password')}
                                                    onClick={this.handleClickShowPassword}
                                                    size="large"
                                                    type="reset"
                                                >
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                    <CISecondaryButton
                                        className={submitButtonClass}
                                        type="submit"
                                        id="loginButton"
                                        fullWidth
                                        disabled={disableButton}
                                        isLoading={isLoading}
                                    >
                                        {t('login.submitbutton')}
                                    </CISecondaryButton>
                                </form>
                            );
                        }}
                    />
                    {patientAccess && !patientActivated && (
                        <CISecondaryButton
                            className="new-patient-register"
                            type="submit"
                            id="registerPatient"
                            fullWidth
                            onClick={(): void => {
                                this.props?.handlePatientAccess({
                                    status: 'REGISTRATION_INPROGRESS',
                                });
                                this.props?.setSwitchUserDialogContent('PatientRegistration');
                            }}
                        >
                            {t('login.patient_activate')}
                        </CISecondaryButton>
                    )}

                    {Capacitor.isNativePlatform() ? null : switchingUser ? (
                        <Link className="auth-link" onClick={this.handleSwitchUserForgotLink}>
                            {t('login.site_forgotbutton')}
                        </Link>
                    ) : (
                        <>
                            <Link className="auth-link" href="/forgotUser" id="login_fpwd" variant="body2">
                                {t('login.forgotbutton')}
                            </Link>
                            <Divider />

                            <span className="returning-user">
                                {t('login.new_user')}
                                <Link
                                    className="auth-link activation-link"
                                    href="/activate"
                                    id="activate"
                                    variant="body2"
                                >
                                    {t('login.activate')}
                                </Link>
                            </span>
                        </>
                    )}
                </div>
                <FirstOfflineLoginModal
                    open={isFirstOfflineLoginModal}
                    onClose={this.closeFirstOfflineLoginModal}
                    t={t}
                />
                {!switchingUser && <ChangePassModal open={changePass} onClose={this.closeModal} t={t} />}
                {isLockOutModal && !switchingUser && (
                    <LockouTimeoutDialog
                        onClose={this.closeLockOutModal}
                        timerClose={this.timerClose}
                        isClearTimer={false}
                        t={t}
                    />
                )}
            </>
        );
    }
}

/**
 * Map Redux state to component props
 *
 * @param auth auth returned from Redux
 */
const mapStateToProps = ({ auth }): object => ({ auth });

const mapDispatchToProps = { ...AuthActions };

/**
 * Default export for the component includes router access (for page navigation) and the appropriate translation context
 */

const LoginPage = flow([withPageStatus, withRouter, withTranslate('auth'), withAuthLayout])(
    connect(mapStateToProps, mapDispatchToProps)(LoginPageComponent)
);

export default LoginPage;
//Should be user for Switch User
export const LoginComponent = flow([withPageStatus, withRouter, withTranslate('auth')])(
    connect(mapStateToProps, mapDispatchToProps)(LoginPageComponent)
);
